<script setup lang="ts">
import { computed, h } from 'vue';
import { useRouter } from 'vue-router';
import { parseISO } from 'date-fns';
import type { ColumnDef } from '@tanstack/vue-table';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ColumnVisibilityMenu, FacetedDataTable } from '@/components/ui/data-table';
import { SearchInput } from '@/components/ui/input';
import {
    lotFraction,
    lotTitle,
    countChipKind,
    countPercent,
    drugUnitLabel,
    formatPercent,
    formatQuantity,
    PERCENT_TONE_CLASSES,
    PERCENT_TONE_TEXT_CLASSES,
    percentTone,
    signedNumber,
    signTone,
} from '@/composables/inventory/inventoryTable';
import { useColumnVisibilityOverrides } from '@/composables/useColumnVisibilityOverrides';
import { i18n } from '@/plugins/internationalization/i18n';
import type { MedicineInventoryRow } from '@/types';
import InventoryActions from './InventoryActions.vue';
import { useInventoryList } from './useInventoryList';

const router = useRouter();
const { columnFilters, facetOptions, isError, isFetching, refetch, rows, search, sorting, visibleRows } = useInventoryList();

const heading = (key: string) => i18n.global.t(`views.health.inventory.table.headings.${key}`);

/** A header drawn as two stacked lines, from the translation's own line break; the column menu and the facet title keep the one-line label. */
function brokenHeading(key: string, align: 'left' | 'right' = 'left') {
    return () => {
        const lines = i18n.global.t(`views.health.inventory.table.headingBreaks.${key}`).split('\n');

        return h(
            'span',
            { class: ['flex flex-col leading-tight', align === 'right' ? 'items-end text-right' : 'items-start text-left'] },
            lines.map((line: string) => h('span', line))
        );
    };
}

type ToggleColumnId = 'opening' | 'bought' | 'used' | 'wasted' | 'adjustment' | 'last_purchase' | 'last_count';

const TOGGLE_COLUMN_IDS: readonly ToggleColumnId[] = ['opening', 'bought', 'used', 'wasted', 'adjustment', 'last_purchase', 'last_count'];

const TOGGLE_HEADINGS: Record<ToggleColumnId, string> = {
    opening: 'opening',
    bought: 'bought',
    used: 'used',
    wasted: 'wasted',
    adjustment: 'adjustment',
    last_purchase: 'lastPurchase',
    last_count: 'lastCount',
};

/**
 * Spillage and the date of the last purchase are left off by default, so the rest fit beside the
 * sidebar; spillage stays a toggle so the row can still be summed to its balance. The farm, drug and
 * balance always show.
 */
const DEFAULT_HIDDEN: readonly ToggleColumnId[] = ['wasted', 'last_purchase'];

const { isVisible, toggleColumn, setAllColumns, columnToggleOptions } = useColumnVisibilityOverrides<ToggleColumnId>({
    storageKey: 'health.inventory.stock.columnVisibility',
    columnIds: TOGGLE_COLUMN_IDS,
    isAvailable: () => true,
    defaultVisible: (id) => !DEFAULT_HIDDEN.includes(id),
    label: (id) => heading(TOGGLE_HEADINGS[id]),
});

/** Tighter side padding than the table default, to fit as many columns as possible beside the sidebar; the header is a little taller so two-line labels fit. */
const COMPACT = { headerClass: 'px-1.5 h-16', cellClass: 'px-1.5' };

const allColumns = computed<ColumnDef<MedicineInventoryRow>[]>(() => [
    {
        id: 'farm',
        accessorKey: 'farm_name',
        header: heading('farm'),
        enableSorting: false,
        meta: { headerClass: 'h-16' },
    },
    {
        id: 'drug',
        accessorKey: 'drug_name',
        header: heading('drug'),
        enableSorting: false,
        meta: COMPACT,
    },
    {
        id: 'opening',
        accessorKey: 'opening',
        header: brokenHeading('opening', 'right'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'bought',
        accessorKey: 'bought',
        header: heading('bought'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'used',
        accessorKey: 'used',
        header: heading('used'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'wasted',
        accessorKey: 'wasted',
        header: heading('wasted'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'adjustment',
        accessorKey: 'adjustment',
        header: heading('adjustment'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'closing',
        accessorKey: 'closing',
        header: heading('closing'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'last_purchase',
        accessorFn: (row) => row.last_purchase_date ?? '',
        header: brokenHeading('lastPurchase'),
        enableSorting: true,
        meta: COMPACT,
    },
    {
        id: 'last_count',
        accessorFn: (row) => row.last_count?.date ?? '',
        header: brokenHeading('lastCount'),
        enableSorting: false,
        meta: COMPACT,
    },
    {
        id: 'percent',
        accessorFn: (row) => countPercent(row.last_count) ?? -1,
        header: heading('percent'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
]);

const columns = computed(() => allColumns.value.filter((column) => !TOGGLE_COLUMN_IDS.includes(column.id as ToggleColumnId) || isVisible(column.id as ToggleColumnId)));

const facets = computed(() => ({
    farm: { title: heading('farm'), options: facetOptions.value.farm ?? [], selectAll: true },
    drug: { title: heading('drug'), options: facetOptions.value.drug ?? [] },
    last_count: { title: heading('lastCount'), options: facetOptions.value.last_count ?? [] },
}));

function formatDate(date: string): string {
    return i18n.global.d(parseISO(date), 'short');
}

function detailRouteOf(row: MedicineInventoryRow) {
    return { name: 'HealthInventoryDetail', params: { farmId: row.farm_id, drugId: row.drug_id, unitId: row.unit_id } };
}

function openDetail(row: MedicineInventoryRow) {
    router.push(detailRouteOf(row));
}

/** Every row opens its detail page, so every row reads as clickable. */
function rowClassOf(): string {
    return 'cursor-pointer';
}

function rowIdOf(row: MedicineInventoryRow): string {
    return `${row.farm_id}:${row.drug_id}:${row.unit_id}`;
}
</script>

<template>
    <MainViewCard class="overflow-hidden">
        <!-- Always shown, so a farm with no stock yet can still register its first purchase or count. -->
        <div class="px-4 pt-4 pb-2 flex flex-wrap items-center justify-between gap-4">
            <h2 class="text-lg font-medium">{{ $t('views.health.inventory.table.stockHeading') }}</h2>
            <div class="flex flex-wrap items-center gap-2">
                <SearchInput v-if="!isError && rows.length > 0" v-model="search" class="w-64" :placeholder="$t('views.health.inventory.table.searchPlaceholder')" />
                <InventoryActions />
                <ColumnVisibilityMenu
                    v-if="!isError && rows.length > 0"
                    icon-only
                    :options="columnToggleOptions"
                    :label="$t('views.health.inventory.table.columnsButton')"
                    @toggle="toggleColumn"
                    @select-all="setAllColumns(true)"
                    @deselect-all="setAllColumns(false)"
                />
            </div>
        </div>
        <div v-if="isError" class="py-14 text-center px-4 text-gray-500 text-lg space-y-4">
            <div>{{ $t('views.pigs.records.errors.couldNotLoadData') }}</div>
            <Button variant="outline" @click="refetch()">
                {{ $t('views.pigs.records.errors.retry') }}
            </Button>
        </div>
        <div v-else-if="!isFetching && rows.length === 0" class="py-14 px-4 text-center space-y-2">
            <div class="text-lg font-medium text-gray-700">{{ $t('views.health.inventory.empty.title') }}</div>
            <div class="mx-auto max-w-lg text-gray-500">{{ $t('views.health.inventory.empty.hint') }}</div>
        </div>
        <div v-else>
            <FacetedDataTable
                v-model:sorting="sorting"
                v-model:column-filters="columnFilters"
                :data="visibleRows"
                :columns="columns"
                :facets="facets"
                :loading="isFetching"
                :enable-pagination="false"
                :page="1"
                :page-size="Math.max(visibleRows.length, 1)"
                :page-size-options="[]"
                max-body-height="max-h-[calc(100vh-21rem)]"
                :row-id="rowIdOf"
                :row-class="rowClassOf"
                @row-click="openDetail"
            >
                <template #item[farm]="{ item }">{{ item.farm_name }}</template>
                <template #item[drug]="{ item }">
                    <!-- The link is what keyboards and screen readers open; a click anywhere else on the row does the same. -->
                    <RouterLink :to="detailRouteOf(item)" class="font-medium rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700" @click.stop>
                        {{ item.drug_name }}
                    </RouterLink>
                    <span class="text-gray-500"> ({{ item.unit_name }})</span>
                </template>
                <template #item[opening]="{ item }">
                    <span class="whitespace-nowrap">{{ formatQuantity(item.opening) }} {{ item.unit_name }}</span>
                </template>
                <!-- Period totals: a zero still reads as a total, only quieter. -->
                <template #item[bought]="{ item }">
                    <span class="whitespace-nowrap" :class="{ 'text-gray-400': item.bought === 0 }">{{ formatQuantity(item.bought) }} {{ item.unit_name }}</span>
                </template>
                <template #item[used]="{ item }">
                    <span class="whitespace-nowrap" :class="{ 'text-gray-400': item.used === 0 }">{{ formatQuantity(item.used) }} {{ item.unit_name }}</span>
                </template>
                <template #item[wasted]="{ item }">
                    <span class="whitespace-nowrap" :class="{ 'text-gray-400': item.wasted === 0 }">{{ formatQuantity(item.wasted) }} {{ item.unit_name }}</span>
                </template>
                <template #item[adjustment]="{ item }">
                    <!-- No adjustment reads as a quiet dash; a shortfall found at a count is red, a surplus green. -->
                    <span v-if="item.adjustment === 0" class="text-gray-400" aria-hidden="true">–</span>
                    <span v-else class="whitespace-nowrap" :class="signTone(item.adjustment)">{{ signedNumber(item.adjustment, formatQuantity) }} {{ item.unit_name }}</span>
                </template>
                <template #item[closing]="{ item }">
                    <div class="flex flex-col items-end gap-1" :title="lotTitle(item)">
                        <span class="whitespace-nowrap font-semibold" :class="{ 'text-red-600': item.closing < 0 }" :title="drugUnitLabel(item)">{{ formatQuantity(item.closing) }} {{ item.unit_name }}</span>
                        <Badge v-if="!item.has_purchase" variant="outline" class="whitespace-nowrap font-medium" :class="PERCENT_TONE_CLASSES.error">
                            {{ $t('views.health.inventory.chips.consumptionWithoutPurchase') }}
                        </Badge>
                        <div v-else class="h-1.5 w-16 overflow-hidden rounded bg-gray-200">
                            <div class="h-full rounded bg-primary-700" :style="{ width: `${item.active_lot ? lotFraction(item.active_lot) * 100 : 0}%` }"></div>
                        </div>
                    </div>
                </template>
                <template #item[last_purchase]="{ item }">
                    <span v-if="item.last_purchase_date" class="whitespace-nowrap">{{ formatDate(item.last_purchase_date) }}</span>
                    <span v-else class="text-gray-400" aria-hidden="true">–</span>
                </template>
                <template #item[last_count]="{ item }">
                    <!-- The date, with the difference it found as a quiet second line. -->
                    <div v-if="item.last_count" class="flex flex-col items-start whitespace-nowrap leading-tight">
                        <span>{{ formatDate(item.last_count.date) }}</span>
                        <span class="text-sm text-gray-500">
                            {{ countChipKind(item.last_count) === 'match' ? $t('views.health.inventory.chips.countMatches') : `${signedNumber(item.last_count.difference, formatQuantity)} ${item.unit_name}` }}
                        </span>
                    </div>
                    <span v-else class="text-gray-500">{{ $t('views.health.inventory.chips.noCount') }}</span>
                </template>
                <template #item[percent]="{ item }">
                    <span v-if="countPercent(item.last_count) !== null" class="whitespace-nowrap font-medium" :class="PERCENT_TONE_TEXT_CLASSES[percentTone(countPercent(item.last_count)!)]">
                        {{ formatPercent(countPercent(item.last_count)!) }}
                    </span>
                    <span v-else class="text-gray-400" aria-hidden="true">–</span>
                </template>
            </FacetedDataTable>
        </div>
    </MainViewCard>
</template>
